// Lazy per-level loader for the HSK review datasets. Vocab files sit
// around 45–90 KB gzipped so we fetch on demand — no need to pull all
// four levels on first paint. Result is memoised per session; a failed
// fetch is dropped from the cache so the next call retries.

import type { HskGrammarItem, HskReviewLevel, HskVocabItem } from './types';

const vocabCache = new Map<HskReviewLevel, Promise<HskVocabItem[]>>();
const grammarCache = new Map<HskReviewLevel, Promise<HskGrammarItem[]>>();

export function loadHskVocab(level: HskReviewLevel): Promise<HskVocabItem[]> {
  const hit = vocabCache.get(level);
  if (hit) return hit;
  const p = fetchJson<HskVocabItem[]>(`/data/hsk-review/vocab-${level}.json`);
  vocabCache.set(level, p);
  p.catch(() => vocabCache.delete(level));
  return p;
}

export function loadHskGrammar(level: HskReviewLevel): Promise<HskGrammarItem[]> {
  const hit = grammarCache.get(level);
  if (hit) return hit;
  const p = fetchJson<HskGrammarItem[]>(`/data/hsk-review/grammar-${level}.json`);
  grammarCache.set(level, p);
  p.catch(() => grammarCache.delete(level));
  return p;
}

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { cache: 'force-cache' });
  if (!res.ok) throw new Error(`Failed to load ${url}: ${res.status}`);
  return (await res.json()) as T;
}
